import { useNavigate } from "react-router-dom";
import { useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { APP_VERSION } from "@/lib/constants";
import { usePageTitle } from "@/hooks/use-page-title";

export function NotFoundPage() {
  usePageTitle("Not Found");
  const navigate = useNavigate();

  const handleGoBack = useCallback(() => {
    void navigate(-1);
  }, [navigate]);

  const handleGoDashboard = useCallback(() => {
    void navigate("/dashboard");
  }, [navigate]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      switch (e.key) {
        case "1": e.preventDefault(); handleGoBack(); break;
        case "2": e.preventDefault(); handleGoDashboard(); break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); };
  }, [handleGoBack, handleGoDashboard]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black">
      <div className="w-full max-w-[700px] px-4 md:px-0">
        <div className="rounded-xl border border-[#3a3a3a] bg-[#1c1c1c] shadow-[0_16px_60px_rgba(0,0,0,0.7),0_0_120px_10px_rgba(51,230,102,0.06)] overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center h-12 px-5 bg-[#252525]">
            <div className="flex gap-2">
              <div className="w-[13px] h-[13px] rounded-full bg-[#ff5f57]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#febc2e]" />
              <div className="w-[13px] h-[13px] rounded-full bg-[#28c840]" />
            </div>
            <div className="flex-1 text-center">
              <span className="text-[13px] text-[#6b6b6b] font-mono">
                spectree — not found
              </span>
            </div>
            <div className="w-[54px]" />
          </div>

          <div className="h-px bg-[#333]" />

          {/* Terminal body */}
          <div className="bg-[#111] p-4 md:p-7 font-mono">
            {/* Command line */}
            <div className="flex items-center">
              <span className="text-[14px] font-bold text-green-400">$</span>
              <span className="text-[14px] font-medium text-[#e5e5e5]"> spectree navigate</span>
              <span className="text-[14px] text-[#6b7280]"> --to {typeof window !== "undefined" ? window.location.pathname : "/"}</span>
            </div>

            <div className="h-[18px]" />

            {/* Error line */}
            <div className="flex items-center">
              <span className="text-[15px] font-bold text-amber-400">⚠ </span>
              <span className="text-[14px] font-bold text-red-400"> Error: </span>
              <span className="text-[14px] text-red-400"> Page not found (404)</span>
            </div>

            <div className="h-[6px]" />
            <p className="text-[13px] text-[#9ca3af] pl-4">
              The page you&apos;re looking for doesn&apos;t exist or has been moved.
            </p>

            {/* Divider */}
            <div className="h-6" />
            <div className="h-px bg-[#2a2a2a] w-full" />
            <div className="h-6" />

            <p className="text-[14px] font-medium text-[#d1d5db] mb-4">
              What would you like to do?
            </p>

            <div className="space-y-1">
              <NotFoundOption number="1" color="text-green-400" icon="←" label="Go back" onClick={handleGoBack} />
              <NotFoundOption number="2" color="text-[#deb94a]" icon="⌂" label="Go to dashboard" onClick={handleGoDashboard} />
            </div>

            <div className="h-7" />
            <div className="flex items-center text-[13px]">
              <span className="text-[#9ca3b0]">Press a number to continue </span>
              <span className="text-green-400 animate-terminal-blink">▊</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-[12px] text-[#4b5563] font-sans">
        spectree {APP_VERSION}
      </div>
    </div>
  );
}

function NotFoundOption({
  number,
  color,
  icon,
  label,
  onClick,
}: {
  number: string;
  color: string;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-4 rounded-md px-3 py-2 hover:bg-white/5 transition-colors w-full text-left cursor-pointer"
    >
      <span className="flex items-center font-mono text-[14px]">
        <span className="text-[#555]">[</span>
        <span className={cn("font-bold", color)}>{number}</span>
        <span className="text-[#555]">]</span>
      </span>
      <span className="flex items-center gap-1 font-mono text-[14px]">
        <span className="text-[#888]">{icon}  </span>
        <span className="text-[#e5e5e5]">{label}</span>
      </span>
    </button>
  );
}
